import {
  Accounts,
  AlchemyUniversity,
  Apps,
  Billing,
  Code04,
  Documentation,
  Home02,
  Key02,
  Logs,
  Notify,
  Password01,
  Paymaster,
  Sandbox,
  Spearmint,
  Stars,
  Transactions,
  Usage,
  UserProfile01,
  UsersProfiles01,
  Bell02,
  Grid04,
} from "@alchemy/ui/icons/16";
import { Subgraphs } from "@alchemy/ui/icons/logos";
import { useMemo } from "react";
import { useSelector } from "react-redux";

import { RootState } from "../../../redux/root";

import { FeatureFlagKey } from "../../launchdarkly/featureFlag";
import useLDFeatureFlag from "../../launchdarkly/useLDFeatureFlag";
import { Path } from "@util/paths.ts";
import { AlchemyDocsUrl, Url } from "@util/urls.ts";
import { WaffleFlag, useWaffleFlag } from "@queries/useWaffleFlags";

interface BaseNavItem {
  label: string;
  icon?: React.ElementType;
  highlight?: boolean;
  onClick?: () => void;
}

export interface InternalNavLink extends BaseNavItem {
  to: string;
  exact?: boolean;
}

export interface ExternalNavLink extends BaseNavItem {
  href?: string;
}

export type NavLink = InternalNavLink | ExternalNavLink;

export interface NavItemGroup {
  title: string;
  items: NavLink[];
}

export type NavItem = NavLink | NavItemGroup;

export const isExternalLink = (item: NavItem): item is ExternalNavLink => {
  return (item as ExternalNavLink).href != null;
};

export const isInternalLink = (item: NavItem): item is InternalNavLink => {
  return (item as InternalNavLink).to != null;
};

export const isGroupItem = (item: NavItem): item is NavItemGroup => {
  return (item as NavItemGroup).items != null;
};

const useNavItems = (): NavItem[] => {
  const { data: enableNavRedesign } = useLDFeatureFlag(
    FeatureFlagKey.NavRedesign,
  );

  const user = useSelector((state: RootState) => state.users.currentUser);
  const isBillingAdmin = !!user.value?.is_billing_admin;
  const allowDataPlatform = useSelector(
    (state: RootState) => state.team.team?.value?.flags?.allow_data_platform,
  );

  const isSignerServiceEnabled = useWaffleFlag(WaffleFlag.SignerService);

  return useMemo(() => {
    if (enableNavRedesign) {
      return [
        {
          label: "Home",
          to: Path.Home,
          exact: true,
        },
        {
          label: "Apps",
          to: Path.Apps,
        },
        {
          title: "Data Services",
          items: [
            {
              label: "Webhooks",
              to: Path.Webhooks,
            },
            {
              label: "Subgraphs",
              href: Path.SubgraphsRedirect,
            },
            ...(allowDataPlatform
              ? [
                  {
                    label: "Pipelines",
                    to: Path.DataPlatform,
                  },
                ]
              : []),
          ],
        },
        {
          title: "Wallet Services",
          items: [
            ...(isSignerServiceEnabled
              ? [
                  {
                    label: "Accounts Manager",
                    to: Path.Accounts,
                  },
                ]
              : []),
            {
              label: "Gas Manager",
              to: Path.GasManager,
            },
          ],
        },
        {
          title: "Developer Tools",
          items: [
            {
              label: "Request Logs",
              to: Path.Logs,
            },
            {
              label: "Alerts",
              to: Path.ConfigureAlerts,
            },
            {
              label: "Mempool",
              to: Path.Mempool,
            },
            {
              label: "Sandbox",
              to: Path.Sandbox,
            },
            {
              label: "ChatWeb3",
              to: Path.ChatWeb3,
            },
          ],
        },
        {
          title: "Admin",
          items: [
            {
              label: "Usage",
              to: Path.Usage,
            },
            ...(isBillingAdmin
              ? [
                  {
                    label: "Billing",
                    to: Path.Billing,
                  },
                  {
                    label: "Access Keys",
                    to: Path.AccessKeys,
                  },
                  {
                    label: "JWT Public Keys",
                    to: Path.JWTPublicKeys,
                  },
                ]
              : []),
          ],
        },
      ];
    }

    return [
      {
        label: "Home",
        icon: Home02,
        to: Path.Home,
        exact: true,
      },
      {
        label: "Apps",
        icon: Apps,
        to: Path.Apps,
      },
      {
        title: "Analytics",
        items: [
          {
            label: "Logs",
            icon: Logs,
            to: Path.Logs,
          },
          {
            label: "Mempool",
            icon: Transactions,
            to: Path.Mempool,
          },
        ],
      },
      {
        title: "Tools",
        items: [
          {
            label: "Webhooks",
            icon: Notify,
            to: Path.Webhooks,
          },
          ...(allowDataPlatform
            ? [
                {
                  label: "Pipelines",
                  icon: Grid04,
                  to: Path.DataPlatform,
                },
              ]
            : []),
          {
            label: "Gas Manager",
            icon: Paymaster,
            to: Path.GasManager,
          },
          ...(isSignerServiceEnabled
            ? [
                {
                  label: "Accounts Manager",
                  icon: Accounts,
                  to: Path.Accounts,
                },
              ]
            : []),
          {
            label: "Sandbox",
            icon: Sandbox,
            to: Path.Sandbox,
          },
          {
            label: "ChatWeb3",
            icon: Stars,
            to: Path.ChatWeb3,
          },
          {
            label: "Subgraphs",
            icon: Subgraphs,
            href: Path.SubgraphsRedirect,
          },
          {
            label: "Spearmint",
            icon: Spearmint,
            href: Url.Spearmint,
          },
        ],
      },
      {
        title: "Resources",
        items: [
          {
            label: "Documentation",
            icon: Documentation,
            href: AlchemyDocsUrl.Home,
          },
          {
            label: "University",
            icon: AlchemyUniversity,
            href: Url.University,
          },
          {
            label: "CreateWeb3Dapp",
            icon: Code04,
            href: AlchemyDocsUrl.CreateWeb3Dapp,
          },
        ],
      },
      {
        title: "Admin",
        items: [
          {
            label: "Usage",
            to: Path.Usage,
            icon: Usage,
          },
          ...(isBillingAdmin
            ? [
                {
                  label: "Billing",
                  icon: Billing,
                  to: Path.Billing,
                },
              ]
            : []),
          {
            label: "Team",
            icon: UsersProfiles01,
            to: Path.Team,
          },
          {
            label: "Alerts",
            icon: Bell02,
            to: Path.ConfigureAlerts,
          },
          ...(isBillingAdmin
            ? [
                {
                  label: "Access Keys",
                  to: Path.AccessKeys,
                  icon: Key02,
                },
                {
                  label: "JWT Public Keys",
                  to: Path.JWTPublicKeys,
                  icon: Password01,
                },
              ]
            : []),
          {
            label: "My Profile",
            icon: UserProfile01,
            to: Path.Profile,
          },
        ],
      },
    ];
  }, [
    isSignerServiceEnabled,
    isBillingAdmin,
    allowDataPlatform,
    enableNavRedesign,
  ]);
};

export default useNavItems;
